import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useConfigureTwilioNumber, type TwilioNumber } from '@/entities/twilioNumber'

interface ConfigureDialogProps {
  number: TwilioNumber | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ConfigureDialog({ number, open, onOpenChange }: ConfigureDialogProps) {
  const configure = useConfigureTwilioNumber()

  const [sipAddress, setSipAddress] = useState('')
  const [campaignId, setCampaignId] = useState('')
  const [fromNumber, setFromNumber] = useState('')

  const DEFAULT_SIP_ADDRESS = 'us-east@zpyqk9dh51v.sip.livekit.cloud'

  useEffect(() => {
    if (number?.config) {
      setSipAddress(number.config.sipAddress || DEFAULT_SIP_ADDRESS)
      setCampaignId(number.config.campaignId ?? '')
      setFromNumber(number.config.fromNumber ?? '')
    } else {
      setSipAddress(DEFAULT_SIP_ADDRESS)
      setCampaignId('')
      setFromNumber('')
    }
  }, [number])

  function handleSave() {
    if (!number) return

    const config: Record<string, string> = { sipAddress, campaignId }
    if (fromNumber) config.fromNumber = fromNumber

    configure.mutate(
      { sid: number.sid, config },
      {
        onSuccess: () => {
          toast.success('Configuration saved')
          onOpenChange(false)
        },
        onError: (err) => {
          toast.error(err.message || 'Failed to save configuration')
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px]">
        <DialogHeader>
          <DialogTitle>Configure {number?.phoneNumber}</DialogTitle>
          <DialogDescription>
            Set the SIP routing parameters for this number. Changes take effect immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="sipAddress" className="text-detail">SIP Address</Label>
            <Input
              id="sipAddress"
              placeholder="us-east@zpyqk9dh51v.sip.livekit.cloud"
              value={sipAddress}
              onChange={(e) => setSipAddress(e.target.value)}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="campaignId" className="text-detail">Campaign ID (X-CAMPAIGN-ID)</Label>
            <Input
              id="campaignId"
              placeholder="e.g. 42"
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="fromNumber" className="text-detail">From Number (X-FROM-NUMBER) <span className="text-muted-foreground font-normal">optional</span></Label>
            <Input
              id="fromNumber"
              placeholder="e.g. +15551234567"
              value={fromNumber}
              onChange={(e) => setFromNumber(e.target.value)}
            />
          </div>

        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={configure.isPending}>
            {configure.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
